import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { setChallengeNumber } from './actions';
import { useDispatch, useSelector } from 'react-redux';
import { fireMessageEvent } from '../messenger/utils';
import { MONTHLY_CHALLENGE_TAB_SELECTED } from '../lib/constants';

const NavWrapper = styled.nav`
  position: sticky;
  padding: 0px ${({ theme }) => theme.spacings.giga} ${({ theme }) => theme.spacings.kilo};
  background: ${({ theme }) => theme.colors.black};
  color: #fff;
  font-weight: bold;
  z-index: 1;
  div {
    font-size: ${({ theme }) => theme.typography.text.tera.fontSize};
    font-weight: 700;
  }
`;

const ChallengeList = styled.ol`
  position: relative;
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-x: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const ChallengeItem = styled.li`
  flex-grow: 1;
  padding: ${({ theme }) => theme.spacings.byte};
  :first-child {
    padding: ${({ theme }) => `0 ${theme.spacings.byte} 0 0`};
  }
  :last-child {
    padding: ${({ theme }) => `0 0 0 ${theme.spacings.byte}`};
  }
`;

const ChallengeButton = styled.button`
  box-sizing: content-box;
  font-size: ${({ theme }) => theme.typography.text.giga.fontSize};
  line-height: ${({ theme }) => theme.typography.text.giga.lineHeight};
  padding: ${({ theme }) => `${theme.spacings.byte} ${theme.spacings.giga}`};
  white-space: nowrap;
  height: 16px;
  border-radius: 100px;
  border: 2px ${({ theme, current }) => (current ? theme.colors.g900 : theme.colors.white)} solid;
  background: ${({ selected, theme, current }) =>
    current && selected ? theme.colors.g900 : !current && selected ? theme.colors.white : theme.colors.black};
  color: ${({ selected, theme, current }) => (!current && selected ? theme.colors.black : theme.colors.white)};
`;

const DisabledChallengeButton = styled(ChallengeButton)`
  box-sizing: content-box;
  font-size: ${({ theme }) => theme.typography.text.giga.fontSize};
  line-height: ${({ theme }) => theme.typography.text.giga.lineHeight};
  padding: ${({ theme }) => `${theme.spacings.byte} ${theme.spacings.giga}`};
  white-space: nowrap;
  height: 16px;
  border-radius: 100px;
  background: #131112;
  color: #aaa;
  border: 2px solid #aaa;
`;

const LeaderboardNav = ({ totalChallenges = 8, onChange = () => {} }) => {
  const dispatch = useDispatch();
  const { config, strings, leaderboard } = useSelector((state) => state);
  const { platform, env } = config;
  const pillText = strings['leaderboard/nav/pill'];
  const { selectedChallenge, currentChallenge } = leaderboard;
  const challengeNumber = selectedChallenge || currentChallenge;
  const scrollToView = useCallback(
    (node) => {
      if (node !== null) {
        let width = 0;
        for (let i = 0; i < currentChallenge - 1; i++) {
          width += node.childNodes[i].offsetWidth;
        }
        node.scrollTo(width, 0);
      }
    },
    [currentChallenge],
  );

  const handleSelection = (selection) => {
    fireMessageEvent({
      platform,
      env,
      title: MONTHLY_CHALLENGE_TAB_SELECTED(selection),
    });
    onChange(selection);
    dispatch(setChallengeNumber(selection));
  };

  return (
    <NavWrapper>
      <ChallengeList ref={scrollToView}>
        {Array(totalChallenges)
          .fill(0)
          .map((_, i) => (
            <ChallengeItem key={i}>
              {i < currentChallenge ? (
                <ChallengeButton
                  selected={challengeNumber === i + 1}
                  current={i === currentChallenge - 1}
                  onClick={() => handleSelection(i + 1)}
                >
                  {pillText} {i + 1}
                </ChallengeButton>
              ) : (
                <DisabledChallengeButton disabled>
                  {pillText} {i + 1}
                </DisabledChallengeButton>
              )}
            </ChallengeItem>
          ))}
      </ChallengeList>
    </NavWrapper>
  );
};

LeaderboardNav.propTypes = {
  totalChallenges: PropTypes.number,
  onChange: PropTypes.func,
};

export default LeaderboardNav;
